import { useMemo, useState } from "react";
import * as ceremonyService from "../../services/ceremonyService";

type Props = {
  open: boolean;
  onClose: () => void;
  eventoId: number;
  onCreated: (created: ceremonyService.Ceremonia) => void;
};

function isoAhoraMasUnaHora() {
  const d = new Date();
  d.setHours(d.getHours() + 1);
  return d.toISOString().slice(0, 16); // para input datetime-local
}

function toIsoZ(datetimeLocal: string) {
  // "2026-06-20T21:00" -> ISO con Z
  const d = new Date(datetimeLocal);
  return d.toISOString();
}

function CreateCeremonyModal({ open, onClose, eventoId, onCreated }: Props) {
  const [tipo, setTipo] = useState<ceremonyService.TipoCeremonia>("IGLESIA");
  const [nombre, setNombre] = useState("");
  const [lugarNombre, setLugarNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [lat, setLat] = useState<string>("");
  const [lng, setLng] = useState<string>("");
  const [urlMaps, setUrlMaps] = useState("");
  const [fechaHoraLocal, setFechaHoraLocal] = useState(isoAhoraMasUnaHora());

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const puedeGuardar = useMemo(() => {
    return (
      nombre.trim().length > 0 &&
      lugarNombre.trim().length > 0 &&
      direccion.trim().length > 0 &&
      fechaHoraLocal.trim().length > 0
    );
  }, [nombre, lugarNombre, direccion, fechaHoraLocal]);

  async function guardar() {
    if (!puedeGuardar || loading) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const created = await ceremonyService.crearCeremonia(eventoId, {
        tipo,
        nombre: nombre.trim(),
        lugarNombre: lugarNombre.trim(),
        direccion: direccion.trim(),
        lat: lat.trim() ? Number(lat) : null,
        lng: lng.trim() ? Number(lng) : null,
        urlMaps: urlMaps.trim() ? urlMaps.trim() : null,
        fechaHora: toIsoZ(fechaHoraLocal),
      });

      onCreated(created);
      onClose();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error creando ceremonia");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl rounded-2xl border border-[#473c53]/60 bg-[#231b2e] shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex flex-col">
            <h3 className="text-white text-lg font-bold">Nueva ceremonia</h3>
            <p className="text-[#ab9db8] text-sm">
              Completa la información de la ceremonia.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-[#ab9db8] hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Tipo</label>
            <select
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white focus:ring-2 focus:ring-primary/50"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option value="IGLESIA">Iglesia</option>
              <option value="CIVIL">Civil</option>
              <option value="RECEPCION">Recepción</option>
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Fecha y hora</label>
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white focus:ring-2 focus:ring-primary/50"
              value={fechaHoraLocal}
              onChange={(e) => setFechaHoraLocal(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="text-sm font-medium text-white">Nombre</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="Ej: Ceremonia religiosa"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="text-sm font-medium text-white">Lugar</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="Ej: Parroquia San José"
              value={lugarNombre}
              onChange={(e) => setLugarNombre(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="text-sm font-medium text-white">Dirección</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="Ej: Av. X 123, Lima"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Latitud</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="-12.104500"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Longitud</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="-77.035000"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="text-sm font-medium text-white">URL de Google Maps</label>
            <input
              className="w-full rounded-lg border border-[#473c53] bg-[#191121] text-white placeholder-[#ab9db8] focus:ring-2 focus:ring-primary/50"
              placeholder="https://maps.google.com/?q=..."
              value={urlMaps}
              onChange={(e) => setUrlMaps(e.target.value)}
            />
          </div>

          {errorMsg && (
            <div className="md:col-span-2 text-sm text-red-400">{errorMsg}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-5 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!puedeGuardar || loading}
            onClick={guardar}
            className="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
          >
            {loading ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateCeremonyModal;